import type {
  DashboardData,
  Stock,
} from '../hooks/stocksHooks/useDashboardData';
import type { StockData } from '../types/stockDataTypes';

export type YahooClosePrices = Record<string, number>;

export const normalizePriceTicker = (ticker?: string | null) =>
  String(ticker ?? '')
    .trim()
    .toUpperCase();

export const uniquePriceTickers = (tickers: Array<string | null | undefined>) =>
  Array.from(new Set(tickers.map(normalizePriceTicker).filter(Boolean))).sort();

export const getYahooClose = (
  prices: YahooClosePrices | undefined,
  ticker?: string | null
): number | null => {
  if (!prices) return null;

  const normalized = normalizePriceTicker(ticker);
  const baseTicker = normalized.endsWith('.US')
    ? normalized.slice(0, -3)
    : normalized.split('.')[0];
  const rawClose = prices[normalized] ?? prices[baseTicker];
  const close = Number(rawClose);

  return Number.isFinite(close) && close > 0 ? close : null;
};

export const applyYahooCloseToStock = (
  stock: Stock,
  prices: YahooClosePrices | undefined
): Stock => {
  const close = getYahooClose(prices, stock.ticker);
  if (close == null) return stock;

  return {
    ...stock,
    currentPrice: close,
    lastPrice: close,
  };
};

export const applyYahooClosesToDashboard = (
  dashboard: DashboardData,
  prices: YahooClosePrices | undefined
): DashboardData => ({
  topGainers: dashboard.topGainers.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
  redDay: dashboard.redDay.map(stock => applyYahooCloseToStock(stock, prices)),
  megaCaps: dashboard.megaCaps.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
  highDividendYield: dashboard.highDividendYield.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
  upcomingEarnings: dashboard.upcomingEarnings.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
  analystFavorites: dashboard.analystFavorites.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
  analystExpectedDrop: dashboard.analystExpectedDrop.map(stock =>
    applyYahooCloseToStock(stock, prices)
  ),
});

export const applyYahooCloseToStockData = (
  stockData: StockData,
  prices: YahooClosePrices | undefined
): StockData => {
  const close = getYahooClose(prices, stockData.ticker);
  if (close == null) return stockData;

  const priceHistory = stockData.PriceHistory || [];
  const nextPriceHistory =
    priceHistory.length > 0
      ? [
          ...priceHistory.slice(0, -1),
          {
            ...priceHistory[priceHistory.length - 1],
            price: close,
          },
        ]
      : [{ date: new Date().toISOString().slice(0, 10), price: close }];

  return {
    ...stockData,
    currentPrice: close,
    lastPrice: close,
    PriceHistory: nextPriceHistory,
  };
};
